import React, { FC, Fragment, memo } from 'react';
import styled from 'styled-components';
import _map from 'lodash/map';
import { useTranslation } from '@blocs.i18n';
import Loader from '@minecraft.loader';
import { helpers } from '@minecraft.utils';
import { DropdownWithChildrenProps } from '../shared/types';
import { useDropdownWithChildrenContext } from '../shared/context';
import { useSearchOptions } from '../shared/hooks';
import DropdownItem from '../DropdownWithChildrenDropdownItem';
import DropdownList from './DropdownWithChildrenDropdownList';

const SELECT_ALL = 'SELECT_ALL';

const StyledDropdownWithChildrenDropdownListContainer = styled.div<{ $heightDropdownAuto: boolean }>`
  position: relative;
  margin-left: -0.5rem;
  margin-right: -0.5rem;
  overflow: auto;

  ${(props) =>
    !props.$heightDropdownAuto &&
    `
    max-height: 4rem;
    min-height: 12.5rem;
  `}
`;

const StyledDropdownSelectAll = styled(DropdownItem)`
  border-bottom: 0.0625px solid ${(props): string => props.theme.form.borderColor};
`;

const StyledFieldLabel = styled.div`
  border-bottom: 0.0625px solid ${(props): string => props.theme.form.borderColor};
  font-size: 0.875rem;
  text-align: inherit;
  padding: 0.5rem;
  width: 100%;
`;

const StyledEmptyResult = styled.div`
  color: ${(props): string => props.theme.colors.primary.grey2};
  font-size: 0.875rem;
  text-align: center;
`;

type Props = Partial<DropdownWithChildrenProps>;

const DropdownWithChildrenDropdownListContainer: FC<Props> = ({
  withSelectAll,
  withAutocomplete,
  heightDropdownAuto,
  isLoading,
  isMultipleSelect,
  isCollapsable,
  selectionsLimit,
  initialOffset,
  initialLimit,
  options,
  onOptionsFetch,
  dataTestId,
}) => {
  const { t } = useTranslation();

  const {
    isSomeOptionsWithFieldName,
    areAllOptionsSelected,
    areAllOptionsDisabled,
    currentSelectedOptions,
    disabledOptions,
    collapsedParentOptions,
    toggleSelectAll,
    toggleParentOptions,
    handleDropdownItemSelect,
  } = useDropdownWithChildrenContext();
  const { visibleOptions } = useSearchOptions({
    withAutocomplete,
    initialOffset,
    initialLimit,
    options,
    onOptionsFetch,
  });

  const groupedOptionsByFieldName = helpers.getGroupedOptionsByFieldName(isSomeOptionsWithFieldName, visibleOptions);

  return (
    <StyledDropdownWithChildrenDropdownListContainer $heightDropdownAuto={heightDropdownAuto}>
      {isLoading && <Loader />}
      {!visibleOptions?.length && !isLoading && <StyledEmptyResult>{t('common:dropdown.noResults')}</StyledEmptyResult>}
      {visibleOptions?.length > 0 && !isLoading && (
        <>
          {withSelectAll && !selectionsLimit && (
            <StyledDropdownSelectAll
              isMultipleSelect={isMultipleSelect}
              isItemSelected={areAllOptionsSelected}
              isItemDisabled={areAllOptionsDisabled}
              checkboxEnable
              item={{ value: SELECT_ALL, label: t('common:form.label.selectAll') }}
              onDropdownItemSelect={toggleSelectAll}
            />
          )}
          {isSomeOptionsWithFieldName ? (
            _map(groupedOptionsByFieldName.values, (groupedOption) => (
              <Fragment key={groupedOption.fieldName}>
                {groupedOption?.fieldLabel && <StyledFieldLabel>{groupedOption.fieldLabel}</StyledFieldLabel>}
                <DropdownList
                  isMultipleSelect={isMultipleSelect}
                  isCollapsable={isCollapsable}
                  selectionsLimit={selectionsLimit}
                  options={groupedOption.options}
                  selectedOptions={currentSelectedOptions}
                  disabledOptions={disabledOptions}
                  collapsedParentOptions={collapsedParentOptions}
                  toggleParentOptions={toggleParentOptions}
                  handleDropdownItemSelect={handleDropdownItemSelect}
                  dataTestId={dataTestId}
                />
              </Fragment>
            ))
          ) : (
            <DropdownList
              isMultipleSelect={isMultipleSelect}
              isCollapsable={isCollapsable}
              selectionsLimit={selectionsLimit}
              options={visibleOptions}
              selectedOptions={currentSelectedOptions}
              disabledOptions={disabledOptions}
              collapsedParentOptions={collapsedParentOptions}
              toggleParentOptions={toggleParentOptions}
              handleDropdownItemSelect={handleDropdownItemSelect}
              dataTestId={dataTestId}
            />
          )}
        </>
      )}
    </StyledDropdownWithChildrenDropdownListContainer>
  );
};

export default memo(DropdownWithChildrenDropdownListContainer);
